import type { CloudAPI } from '@yandex-360/telemost-sdk/cloud-api';
import { call, put, SagaGenerator, select } from 'typed-redux-saga';

import {
    isRecordingAllowedSelector,
    PermissionsState,
    recordingPermissionsDeletedSaga,
    selectHasCameraPermission,
    selectHasDesktopPermission,
    selectHasMicrophonePermission,
    updatePermissions,
} from '.';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import type { PersonalPermission } from '../../../../shared/helpers/permissions';
import {
    getDesktopSharingStatus,
    getSelfMutedState,
    isLocalRecordingActive as selectIsLocalRecordingActive,
    selectIsPermissionsExtendedEnabled,
} from '../../selectors';
import { toggleMuteAction } from '../device/slice';
import { unshareScreenAction } from '../screen-sharing/slice';

const logger = createLogger('goloom:permissions');

export function* updatePermissionsInfoSaga(permissionsInfo: CloudAPI.PermissionsInfo) {
    const permissionsState: PermissionsState = {
        version: permissionsInfo.version,
        publicRolePermissions: permissionsInfo.public_role_permissions,
        personalAllowed: permissionsInfo.personal_allowed as PersonalPermission[],
    };

    yield* call(updatePermissionsEffect, function* () {
        yield* put(updatePermissions(permissionsState));
    });
}

export function* updatePermissionsEffect(changeSaga: () => SagaGenerator<void>) {
    /** Получаем значения прав до обновления */
    const isRecordingAllowedBefore = yield* select(isRecordingAllowedSelector);
    const hasCameraPermissionBefore = yield* select(selectHasCameraPermission);
    const hasDesktopPermissionBefore = yield* select(selectHasDesktopPermission);
    const hasMicrophonePermissionBefore = yield* select(selectHasMicrophonePermission);

    yield* call(changeSaga);

    /** Получаем значения прав после обновления */
    const isRecordingAllowedAfter = yield* select(isRecordingAllowedSelector);
    const hasCameraPermissionAfter = yield* select(selectHasCameraPermission);
    const hasMicrophonePermissionAfter = yield* select(selectHasMicrophonePermission);
    const hasDesktopPermissionAfter = yield* select(selectHasDesktopPermission);

    const isLocalRecordingActive = yield* select(selectIsLocalRecordingActive);

    /** Реагируем на изменения */
    if (isLocalRecordingActive && isRecordingAllowedBefore === true && isRecordingAllowedAfter === false) {
        logger.event('Stop active local recording', {
            additional: {
                method: 'updatePermissionsInfoSaga',
                feature: 'role_model',
            },
        });
        yield* call(recordingPermissionsDeletedSaga);
    }

    const isPermissionsExtendedEnabled = yield* select(selectIsPermissionsExtendedEnabled);
    if (isPermissionsExtendedEnabled) {
        const selfMutedState = yield* select(getSelfMutedState);
        if (selfMutedState && !selfMutedState.videoMuted && hasCameraPermissionBefore && !hasCameraPermissionAfter) {
            yield* put(toggleMuteAction({ value: true, type: 'video' }));
        }

        const desktopSharingStatus = yield* select(getDesktopSharingStatus);
        if (desktopSharingStatus === 'local' && hasDesktopPermissionBefore && !hasDesktopPermissionAfter) {
            yield* put(unshareScreenAction());
        }

        if (
            selfMutedState &&
            !selfMutedState.audioMuted &&
            hasMicrophonePermissionBefore &&
            !hasMicrophonePermissionAfter
        ) {
            yield* put(toggleMuteAction({ value: true, type: 'audio' }));
        }
    }
}
